import { useContext, useEffect, useMemo, useState } from 'react';
import { findNikkeIcon, resolveBuiltinNikkeIconSource } from '../domain/nikkeIcons.js';
import { NikkeBurstGlyph } from './NikkeBurstGlyph.jsx';
import { NikkeIconContext } from './NikkeIconProvider.jsx';

const TEXT_FALLBACKS = {
  combat: '전',
  level: 'Lv',
  core: '★',
  affection: '♥',
  skills: 'S',
  cube: 'C',
  owned: '보',
  costume: '옷',
  equipment: '장',
  overload: 'OL',
  head: '머',
  torso: '몸',
  arm: '팔',
  leg: '다',
  attacker: '화',
  defender: '방',
  supporter: '지',
};

export function NikkeIcon({
  type,
  iconKey,
  aliases = [],
  label = '',
  imageUrl = '',
  fallbackName = 'equipment',
  fallbackText = '',
  className = '',
  forceText = false,
  loading = 'lazy',
  // Portrait burst badges draw their text as an SVG glyph that scales with the badge.
  scalableGlyph = false,
}) {
  const { lookup } = useContext(NikkeIconContext);
  const icon = findNikkeIcon(lookup, type, iconKey, aliases, label);
  const builtinSource = resolveBuiltinNikkeIconSource(type, iconKey, aliases, label, fallbackName);
  const primarySource = icon?.imageUrl || imageUrl;
  const secondarySource = icon?.imageUrl && imageUrl && imageUrl !== icon.imageUrl ? imageUrl : '';
  const candidates = useMemo(() => [...new Set([
    primarySource,
    secondarySource,
    builtinSource,
  ].map((value) => String(value || '').trim()).filter(Boolean))], [builtinSource, primarySource, secondarySource]);
  const candidateKey = candidates.join('\n');
  const [failedSources, setFailedSources] = useState([]);

  useEffect(() => {
    setFailedSources((current) => {
      const next = current.filter((source) => candidates.includes(source));
      return next.length === current.length ? current : next;
    });
  }, [candidateKey, candidates]);

  const visibleSource = forceText ? '' : candidates.find((source) => !failedSources.includes(source)) || '';
  const accessibleLabel = label || icon?.label || String(iconKey || type || '아이콘');
  const dataIconKey = String(iconKey || icon?.iconKey || '').trim().toLocaleLowerCase('en-US');
  const textFallback = fallbackText
    || TEXT_FALLBACKS[dataIconKey]
    || TEXT_FALLBACKS[String(fallbackName || '').toLocaleLowerCase('en-US')]
    || '·';
  return (
    <span className={`nikke-db-icon${visibleSource ? '' : ' is-text-fallback'} ${className}`.trim()} data-icon-type={type} data-icon-key={dataIconKey} title={accessibleLabel} aria-label={accessibleLabel}>
      {visibleSource ? <img src={visibleSource} alt="" loading={loading} decoding="async" onError={() => setFailedSources((current) => current.includes(visibleSource) ? current : [...current, visibleSource])} /> : scalableGlyph ? <NikkeBurstGlyph text={textFallback} /> : <b aria-hidden="true">{textFallback}</b>}
    </span>
  );
}
